import { Stack } from "@mui/material";
import { useAppStateContext } from "@src/appV2/App/useAppState";
import { isCapacitorPlatform, isIosPlatform } from "@src/appV2/lib";
import { useIsDeviceLocationPermissionGranted } from "@src/appV2/Location/deviceLocation/geoLocation";
import { requestFullLocationAccess } from "@src/appV2/Location/utils/permissions";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import {
  BottomSheet,
  CbhIcon,
  Illustration,
  Text,
  Title,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import { useEffect, useRef } from "react";

import { TextButton } from "../../components/TextButton";

interface InterviewLocationPermissionBottomSheetProps {
  modalState: UseModalState;
  onConfirm: () => void;
  onClose?: () => void;
  onContinueClick?: () => void;
  isOsWithoutAlwaysAllow: boolean;
}

export function InterviewLocationPermissionBottomSheet(
  props: InterviewLocationPermissionBottomSheetProps
) {
  const { modalState, onConfirm, onClose, onContinueClick, isOsWithoutAlwaysAllow } = props;
  const hasNavigatedToSettings = useRef(false);
  const {
    state: { isAppActive },
  } = useAppStateContext();
  const { refetch: refetchLocationPermission } = useIsDeviceLocationPermissionGranted();

  const handleGoToSettings = () => {
    hasNavigatedToSettings.current = true;
    onContinueClick?.();
    if (isCapacitorPlatform()) {
      void requestFullLocationAccess();
    } else {
      onConfirm();
    }
  };

  useEffect(() => {
    if (!isAppActive || !hasNavigatedToSettings.current) {
      return;
    }

    hasNavigatedToSettings.current = false;

    async function checkPermission(): Promise<void> {
      const { data: isPermissionGranted } = await refetchLocationPermission();

      if (isPermissionGranted) {
        modalState.closeModal();
        onConfirm();
      }
    }

    void checkPermission();
  }, [isAppActive, modalState, onConfirm, refetchLocationPermission]);

  return (
    <BottomSheet
      modalState={modalState}
      footer={
        <DialogFooter orientation="vertical">
          <LoadingButton
            fullWidth
            variant="contained"
            size="large"
            isLoading={false}
            onClick={handleGoToSettings}
          >
            Go to settings
          </LoadingButton>
        </DialogFooter>
      }
      onClose={() => {
        modalState.closeModal();
        onClose?.();
      }}
    >
      <Stack direction="column" alignItems="center" px={6} pb={10} spacing={8}>
        <Illustration type="location" />

        <Title variant="h3" component="h3" textAlign="center">
          Enable location permissions
        </Title>

        <Text variant="body1" textAlign="center">
          You must enable the{" "}
          {isOsWithoutAlwaysAllow ? (
            <>
              <b>&quot;Location&quot;</b> permission in your settings
            </>
          ) : (
            <>
              <b>&quot;{isIosPlatform() ? "Always" : "Always and Precise"}&quot;</b> option in your
              location settings
            </>
          )}{" "}
          to book an interview through the Clipboard job board. We collect and use this location
          information to:
        </Text>

        <Stack direction="column" px={4} spacing={6} textAlign="left">
          <Stack alignItems="center" direction="row" spacing={4}>
            <CbhIcon type="building" />
            <Text bold variant="body1">
              Let workplaces know you&apos;re on your way and help avoid check-in issues
            </Text>
          </Stack>
          <Stack alignItems="center" direction="row" spacing={4}>
            <CbhIcon type="phone" />
            <Text bold variant="body1">
              Confirm you attended the interview without needing to call or text you
            </Text>
          </Stack>
          <Stack alignItems="center" direction="row" spacing={4}>
            <CbhIcon type="star" />
            <Text bold variant="body1">
              Help you hear back faster about your application next steps
            </Text>
          </Stack>
        </Stack>

        <TextButton size="small" href="https://clipboardhealth.com/privacy-policy">
          Read our privacy policy
        </TextButton>
      </Stack>
    </BottomSheet>
  );
}
